import React, { FC, useState, Suspense } from 'react';
import classNames from 'classnames';
import loadable from '@loadable/component';
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/types/constants/navConstants';

import Hamburger from 'components/nav/TopNav/Hamburger';
import CartContainer from 'components/nav/TopNav/CartContainer';
import NavLogo from 'components/navV2/NavLogo';
import { toggleDocumentOverlayClass, toggleClassNameOnElements } from 'util/overlayHelper';
import {
  WEB_WEDDING_CONTENT_ID,
  WEB_WEDDING_SUB_NAV_CLASSNAME,
  WEB_ZOLA_CONTENT_CLASSNAME,
  WEB_ZOLA_FOOTER_ID,
  WEB_NAV_ID,
} from 'util/constants/documentConstants';
import { UniversalTabs } from 'reducers/nav';
import RebrandLogoWhite from '@zola/zola-ui/src/assets/images/rebrand/zola-logomark-cloud.svg';
import { isLoggedIn } from 'util/authHelpers';
import experimentFlags, { HOLIDAY_SHOPS } from 'util/experimentFlags';
import MobileNavData from './MobileNavData';
import Notification from '../nav/TopNav/Notification';
import type { MobileNavV2MapStateProps } from './index';

import './mobileNavV2.less';

import styles from './MobileNavV2.module.less';

const MobileNavMenuContent = loadable(
  () =>
    import(
      /* webpackChunkName: "mobile-menu-context" */ 'components/mobileNavV2/MobileNavMenuContent'
    ),
  { ssr: false }
);

const SearchContainer = loadable(
  () => import(/* webpackChunkName: "search-container" */ 'components/nav/Search/SearchContainer'),
  { ssr: false }
);

type MobileNavV2Props = MobileNavV2MapStateProps & {
  primaryLinkId: PrimaryNavIdentifier | '';
};

const MobileNavV2: FC<MobileNavV2Props> = ({ userContext, activeUniversalTab, primaryLinkId }) => {
  const [hamburgerActive, setHamburgerActive] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const navData = new MobileNavData(userContext);
  const isShopHolidaySeason = experimentFlags.getFlag(HOLIDAY_SHOPS, userContext);
  const registryShopLinks = navData.getRegistryShopLinks(isShopHolidaySeason);
  const isHomeStoreActive =
    activeUniversalTab === UniversalTabs.HOME_STORE ||
    (typeof window !== 'undefined' && window.location.href.indexOf('homestore') >= 0);
  const headerContentClasses = classNames(styles.headerContent, {
    [styles.blueBg]: isHomeStoreActive,
  });
  const mobileContentClasses = classNames('nav-mobile__content', {
    [styles.blueBg]: isHomeStoreActive,
  });

  const determineZolaLogo = () => {
    return RebrandLogoWhite;
  };
  const logoProp = isHomeStoreActive ? { logo: determineZolaLogo() } : {};

  const toggleSearch = () => {
    setSearchActive(!searchActive);
    toggleDocumentOverlayClass(!searchActive, 'unified-nav__search-menu-open');
  };

  const toggleMenu = () => {
    setHamburgerActive(!hamburgerActive);
    if (searchActive) toggleSearch();

    const webZolaContent =
      document.getElementsByClassName(WEB_ZOLA_CONTENT_CLASSNAME) &&
      document.getElementsByClassName(WEB_ZOLA_CONTENT_CLASSNAME)[0];
    const webWeddingsContent = document.getElementById(WEB_WEDDING_CONTENT_ID);
    const footerContent = document.getElementById(WEB_ZOLA_FOOTER_ID);
    // web-wedding post auth manage pages sometimes have 2 sub navs;
    const subNavEls = document.querySelectorAll(`.${WEB_WEDDING_SUB_NAV_CLASSNAME}`);
    const webNavEl = document.getElementById(WEB_NAV_ID);
    // in case another app needs to know that mobile nav is currently active
    const bodyEl = document.body;

    const shouldAppend = !hamburgerActive;
    toggleClassNameOnElements(shouldAppend, 'unified-nav__fixed', [
      webZolaContent,
      footerContent,
      webWeddingsContent,
      ...subNavEls,
    ]);
    if (shouldAppend) {
      // tell web-nav you are looking at mobile-menu-open view;
      if (webNavEl && bodyEl) {
        webNavEl.classList.add('unified-nav__mobile-menu-open');
        bodyEl.classList.add('unified-nav__mobile-menu-open');
      }
    } else if (webNavEl && bodyEl) {
      webNavEl.classList.remove('unified-nav__mobile-menu-open');
      bodyEl.classList.remove('unified-nav__mobile-menu-open');
    }
  };

  return (
    <nav className="visible-xs-block visible-sm-block nav-mobile__container top-nav__container-v2 navV2__mobile-container">
      <div className={mobileContentClasses}>
        <div className={headerContentClasses}>
          <div className="nav-mobile__left">
            <Hamburger
              numberOfSpans={4}
              isActive={hamburgerActive}
              toggleActive={toggleMenu}
              isHomeStoreActive={isHomeStoreActive}
            />
            <i
              className={classNames(styles.searchIcon, 'zola-ui-icon-magnifying-glass', {
                [styles.hidden]: hamburgerActive,
                [styles.isHomeStore]: isHomeStoreActive,
              })}
              onClick={toggleSearch}
            />
          </div>
          <NavLogo {...logoProp} />
          <div className="nav-mobile__right">
            {!isHomeStoreActive && isLoggedIn(userContext) && userContext?.object_id && (
              <Notification isNav userId={userContext.object_id} className={styles.iconContainer} />
            )}
            <CartContainer
              isNavV2
              className={classNames(styles.iconContainer, {
                [styles.homeStoreActive]: isHomeStoreActive,
              })}
            />
          </div>
        </div>
        {hamburgerActive && (
          <Suspense fallback={<div>loading mobile menu</div>}>
            <div className={classNames('nav-mobile__menu-container', { active: hamburgerActive })}>
              <ul className="nav-mobile__menu-content">
                <MobileNavMenuContent
                  primaryLinkId={primaryLinkId}
                  isShopHolidaySeason={isShopHolidaySeason}
                />
              </ul>
            </div>
          </Suspense>
        )}
        {searchActive && (
          <Suspense fallback={<div>loading search result</div>}>
            <div
              className={classNames('nav-mobile__search', {
                'nav-mobile__search-active': searchActive,
              })}
            >
              <SearchContainer
                isActive={searchActive}
                storeLinks={registryShopLinks}
                toggleSearchActive={toggleSearch}
                hideToggle={isHomeStoreActive}
                notDesktop
                isWeddingStoreActive={!isHomeStoreActive}
              />
            </div>
          </Suspense>
        )}
      </div>
    </nav>
  );
};

export default MobileNavV2;
